import * as React from 'react';
import Form from 'antd/lib/form';
import Select from 'antd/lib/select';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import { FilterOption } from '@/types/summaryView';
import { isArchetypeProperty, isStatisticProperty, getSummaryViewPropertyLabel } from '@/helpers/summaryViewHelpers';
import { antGrey } from '@/components/styles/globalStyles';
const { Option, OptGroup } = Select;

interface FiltersPopoverProps {
    isVisible: boolean;
    label: 'Rows' | 'Columns';
    onApply: (metrics: string[], versions: string[]) => void;
    onTrack?: (metrics: string[], versions: string[]) => void;
    currentMetrics: string[];
    currentVersions: string[];
    availableMetrics: FilterOption[];
    isSubmitting: boolean;
}

const groupMetrics = (metrics: FilterOption[]) => {
    const standardMetrics: FilterOption[] = [];
    const stepDetails: FilterOption[] = [];
    const statistics: FilterOption[] = [];

    metrics.forEach((metric) => {
        if (isArchetypeProperty(metric.key)) {
            stepDetails.push(metric);
        } else if (isStatisticProperty(metric.key)) {
            statistics.push(metric);
        } else {
            standardMetrics.push(metric);
        }
    });

    return { standardMetrics, stepDetails, statistics };
};

export const FiltersPopover: React.FC<FiltersPopoverProps> = (props) => {
    const [form] = Form.useForm();

    const { standardMetrics, stepDetails, statistics } = React.useMemo(
        () => groupMetrics(props.availableMetrics),
        [props.availableMetrics],
    );

    const applyFilters = (values: { metrics?: string[] }) => {
        const metrics = values.metrics || [];
        props.onApply(metrics, props.currentVersions);
        props.onTrack?.(metrics, props.currentVersions);
    };

    const resetFilters = () => {
        form.setFieldsValue({
            metrics: props.currentMetrics,
        });
    };

    const prevIsVisible = React.useRef(false);

    React.useEffect(() => {
        if (props.isVisible && !prevIsVisible.current) {
            form.setFieldsValue({
                metrics: props.currentMetrics,
            });
        }
        prevIsVisible.current = props.isVisible;
    }, [props.isVisible, props.currentMetrics, form]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <div
                style={{
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingTop: 18,
                    paddingBottom: 18,
                    maxHeight: 450,
                    overflowY: 'scroll',
                }}
            >
                <Form
                    form={form}
                    initialValues={{
                        metrics: props.currentMetrics,
                    }}
                    onFinish={applyFilters}
                    preserve={false}
                    layout="vertical"
                >
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Flex vertical>
                            <Flex style={{ paddingBottom: 8 }} justify="space-between">
                                <Text style={{ fontSize: 14, color: antGrey.grey8 }}>{props.label}</Text>
                                <Button
                                    type="link"
                                    onClick={() => {
                                        form.setFieldsValue({ metrics: props.availableMetrics.map((m) => m.key) });
                                    }}
                                    style={{ padding: 0, height: 'fit-content' }}
                                >
                                    Select all
                                </Button>
                            </Flex>
                            <Form.Item name="metrics" noStyle>
                                <Select
                                    mode="multiple"
                                    placeholder={`Select ${props.label.toLowerCase()}`}
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    maxTagCount={'responsive'}
                                    style={{ width: 400 }}
                                >
                                    {standardMetrics.length > 0 && (
                                        <OptGroup label="Standard Attributes">
                                            {standardMetrics.map((metric) => (
                                                <Option key={metric.key} value={metric.key}>
                                                    {metric.label}
                                                </Option>
                                            ))}
                                        </OptGroup>
                                    )}
                                    {stepDetails.length > 0 && (
                                        <OptGroup label="Step Details">
                                            {stepDetails.map((metric) => (
                                                <Option key={metric.key} value={metric.key}>
                                                    {getSummaryViewPropertyLabel(metric.key)}
                                                </Option>
                                            ))}
                                        </OptGroup>
                                    )}
                                    {statistics.length > 0 && (
                                        <OptGroup label="Statistics">
                                            {statistics.map((metric) => (
                                                <Option key={metric.key} value={metric.key}>
                                                    {metric.label}
                                                </Option>
                                            ))}
                                        </OptGroup>
                                    )}
                                </Select>
                            </Form.Item>
                        </Flex>
                    </Space>
                </Form>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 8,
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingTop: 12,
                    paddingBottom: 12,
                    backgroundColor: antGrey.grey3,
                }}
            >
                <Button size="small" onClick={resetFilters} disabled={props.isSubmitting}>
                    Reset
                </Button>
                <Button size="small" onClick={form.submit} type="primary" loading={props.isSubmitting}>
                    Apply
                </Button>
            </div>
        </div>
    );
};
